import { loadStripe } from '@stripe/stripe-js';
import { getProMonthlyEmailLimit } from './proEmailLimits';
import {
  PRO_MONTHLY_PRICING_TIERS,
  PRO_ANNUAL_PRICING_TIERS,
  getProMonthlyPrice,
  getProAnnualPrice,
} from './proPlanPricing';

// Get Stripe publishable key
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Stripe configuration - only load if key is available
export const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Stripe configuration object
// ⚠️ SECURITY: Secret keys should NEVER be in client-side code!
// Only publishable keys are safe for client-side use.
export const stripeConfig = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  // Removed: secretKey and webhookSecret should only be in backend (Firebase Functions)
  // secretKey: process.env.REACT_APP_STRIPE_SECRET_KEY || '', // ❌ SECURITY RISK - REMOVED
  // webhookSecret: process.env.REACT_APP_STRIPE_WEBHOOK_SECRET || '', // ❌ SECURITY RISK - REMOVED
  // API endpoints
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001',
  // Stripe Elements appearance
  appearance: {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#7c3aed',
      colorBackground: '#ffffff',
      colorText: '#1f2937',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  },
};

// Pricing tiers for PRO plan (re-exported from proPlanPricing.ts)
export const PRO_PRICING_TIERS = PRO_MONTHLY_PRICING_TIERS;
export { PRO_ANNUAL_PRICING_TIERS };

// Business plan — flat rate (unlimited contacts + unlimited emails)
export const BUSINESS_MONTHLY_PRICE = 249;
export const BUSINESS_ANNUAL_PRICE = 2490; // 10 months = 2 months free

/** Single-entry tier kept for compatibility with code that iterates tiers. */
export const BUSINESS_PRICING_TIERS = [
  { subscribers: 0, price: BUSINESS_MONTHLY_PRICE },
];

// Plan configuration
export const PLAN_CONFIG = {
  starter: {
    id: 'starter',
    name: 'Starter',
    price: 0,
    currency: 'usd',
    interval: 'month' as const,
    subscribers: 1000,
    emails: 12000,
    features: [
      'Scubamail branding on emails',
      'Reports available for 30 days',
      'Create up to 3 landing pages and forms',
      'Access for 1 user',
      '1 Workspace'
    ]
  },
  free: {
    id: 'starter',
    name: 'Starter',
    price: 0,
    currency: 'usd',
    interval: 'month' as const,
    subscribers: 1000,
    emails: 12000,
    features: [
      'Scubamail branding on emails',
      'Reports available for 30 days',
      'Create up to 3 landing pages and forms',
      'Access for 1 user',
      '1 Workspace'
    ]
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    price: 4.99, // Unified minimum tier
    currency: 'usd',
    interval: 'month' as const,
    subscribers: 1000, 
    emails: 12000, 
    features: [
      'Tiered monthly email sends (fair use; limit by contact tier — see pricing)',
      'Reports available forever',
      'Unlimited landing pages and forms',
      'Add unlimited users',
      'Cancel any time',
      'Up to 3 Workspaces',
      'Custom Domains included'
    ],
    // Pricing tiers based on subscribers
    pricingTiers: PRO_PRICING_TIERS
  },
  business: {
    id: 'business',
    name: 'Business',
    price: BUSINESS_MONTHLY_PRICE,
    annualPrice: BUSINESS_ANNUAL_PRICE,
    currency: 'usd',
    interval: 'month' as const,
    subscribers: -1, // unlimited
    emails: 1_000_000_000,
    features: [
      'Unlimited monthly email sends (fair use)',
      'Reports available forever',
      'Unlimited landing pages and forms',
      'Add unlimited users',
      'Unlimited Workspaces',
      'Custom Domains included',
      'Priority support'
    ],
    pricingTiers: BUSINESS_PRICING_TIERS
  }
};

// Calculate price based on subscribers for Pro plan
export const calculateProPrice = (subscribers: number, currency: string = 'usd') => {
  const price = getProMonthlyPrice(subscribers);

  return {
    price,
    currency: currency.toLowerCase(),
    subscribers: subscribers,
    emails: getProMonthlyEmailLimit(subscribers),
  };
};

export const calculateProAnnualPriceSync = (subscribers: number): number =>
  getProAnnualPrice(subscribers);

// Calculate price based on plan type and subscribers
// This function now uses the pricing service with fallback to defaults
export const calculatePlanPrice = async (plan: 'pro' | 'business', subscribers: number): Promise<number> => {
  try {
    const { planPricingService } = await import('../services/planPricingService');
    return await planPricingService.getPriceForSubscribers(plan, subscribers);
  } catch (error) {
    console.warn('Failed to fetch pricing from service, using defaults:', error);
    // Pro uses tiers; Business is flat rate
    if (plan === 'business') return BUSINESS_MONTHLY_PRICE;
    const tiers = PRO_PRICING_TIERS;

    // If subscribers is less than the minimum tier (500), use the first tier
    if (subscribers < tiers[0].subscribers) {
      return tiers[0].price;
    }

    // Find the appropriate tier for the subscriber count
    const tier = tiers.find(tier => subscribers <= tier.subscribers) || tiers[tiers.length - 1];
    return tier.price;
  }
};

// Synchronous version for backwards compatibility (uses defaults)
export const calculatePlanPriceSync = (plan: 'pro' | 'business', subscribers: number): number => {
  if (plan === 'pro') {
    return getProMonthlyPrice(subscribers);
  }
  // Business is a flat rate — ignore subscriber count
  return BUSINESS_MONTHLY_PRICE;
};

export const calculatePlanAnnualPriceSync = (
  plan: 'pro' | 'business',
  subscribers: number
): number => {
  if (plan === 'pro') {
    return getProAnnualPrice(subscribers);
  }
  // Business flat annual price
  return BUSINESS_ANNUAL_PRICE;
};

// Currency symbols (enhanced with more currencies)
export const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: '$',
  inr: '₹',
  eur: '€',
  gbp: '£',
  cad: 'C$',
  aud: 'A$',
  sgd: 'S$',
  aed: 'د.إ',
  jpy: '¥',
};

// Stripe Elements options
export const ELEMENTS_OPTIONS = {
  mode: 'payment' as const,
  currency: 'usd',
  amount: 0, // Will be set dynamically
  appearance: stripeConfig.appearance,
  locale: 'en' as const,
};
